import React, { useEffect, useState, useCallback } from 'react';
// Import API service functions
import {
    getAllMissions,
    deleteMission,
    createMission,
    updateMission
} from '../../services/apiService'; // Sesuaikan path
// Impor CSS
import './MissionManagement.css'; // Pastikan path benar

// --- Initial Form State ---
const initialMissionFormData = {
    mission_title: '',
    mission_SBtitle: '',
    mission_description: '',
    task_title: [''],
    task_points: [''],
};

// --- Helper Components ---
const LoadingSpinner = ({ small = false }) => (
    <div className={`loading-spinner ${small ? 'small' : ''}`}></div>
);
const ProcessingIndicator = ({ message = "Processing..." }) => (
    <div className="processing-indicator">
        <LoadingSpinner small /> {message}
    </div>
);
const ErrorMessage = ({ message }) => (
    <div className="error-message" role="alert">
        <strong>Error: </strong><span>{message}</span>
    </div>
);
// SuccessMessage mungkin tidak terlalu dibutuhkan di sini lagi
// const SuccessMessage = ({ message }) => (
//     <div className="success-message" role="alert">
//         <strong>Success: </strong><span>{message}</span>
//     </div>
// );

// --- Main Component ---
function MissionManagement() {
    const [missions, setMissions] = useState([]);
    const [loadingMissions, setLoadingMissions] = useState(true);
    const [missionError, setMissionError] = useState('');
    const [missionProcessing, setMissionProcessing] = useState(false); // Untuk delete
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentMissionId, setCurrentMissionId] = useState(null);
    const [missionFormData, setMissionFormData] = useState(initialMissionFormData);
    const [formError, setFormError] = useState('');
    const [formProcessing, setFormProcessing] = useState(false); // Untuk add/edit submit

    // --- Fetch Missions ---
    const fetchMissions = useCallback(async () => {
        setLoadingMissions(true);
        setMissionError('');
        try {
            const responseData = await getAllMissions();
            const mappedMissions = responseData.data.map(apiMission => ({
                id: apiMission.id,
                name: apiMission.mission_title,
                points: (apiMission.task_points && apiMission.task_points.length > 0) ? apiMission.task_points[0] : 0,
                _originalData: apiMission
            }));
            setMissions(mappedMissions || []);
        } catch (err) {
            setMissionError(err.message || 'Gagal memuat daftar misi.');
            console.error("Fetch missions error:", err);
        } finally {
            setLoadingMissions(false);
        }
    }, []);

    useEffect(() => {
        fetchMissions();
    }, [fetchMissions]);

    // --- Mission Modal Handlers (Sama seperti sebelumnya) ---
    const openMissionModal = (editMode = false, missionData = null) => {
        setIsEditMode(editMode);
        setFormError('');
        if (editMode && missionData) {
            setCurrentMissionId(missionData.id);
            setMissionFormData({
                mission_title: missionData._originalData.mission_title || '',
                mission_SBtitle: missionData._originalData.mission_SBtitle || '',
                mission_description: missionData._originalData.mission_description || '',
                task_title: Array.isArray(missionData._originalData.task_title) ? missionData._originalData.task_title : [''],
                task_points: Array.isArray(missionData._originalData.task_points) ? missionData._originalData.task_points.map(String) : [''],
            });
        } else {
            setCurrentMissionId(null);
            setMissionFormData(initialMissionFormData);
        }
        setIsModalOpen(true);
    };

    const closeMissionModal = () => {
        setIsModalOpen(false);
    };

    const handleMissionInputChange = (e) => {
        const { name, value } = e.target;
        setMissionFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleMissionTaskChange = (index, field, value) => {
        setMissionFormData(prev => {
            const newTasks = [...prev[field]];
            newTasks[index] = value;
            if (field === 'task_points') {
                newTasks[index] = value === '' ? '' : value;
            }
            return { ...prev, [field]: newTasks };
        });
    };

    const addMissionTaskField = () => {
        setMissionFormData(prev => ({
            ...prev,
            task_title: [...prev.task_title, ''],
            task_points: [...prev.task_points, '']
        }));
    };

    const removeMissionTaskField = (index) => {
        if (missionFormData.task_title.length <= 1) return;
        setMissionFormData(prev => ({
            ...prev,
            task_title: prev.task_title.filter((_, i) => i !== index),
            task_points: prev.task_points.filter((_, i) => i !== index)
        }));
    };

    const handleMissionFormSubmit = async (e) => {
        e.preventDefault();
        setFormProcessing(true);
        setFormError('');
        if (!missionFormData.mission_title.trim()) {
            setFormError('Nama Misi tidak boleh kosong.');
            setFormProcessing(false); return;
        }
        const apiData = { /* ... data preparation logic ... */
            ...missionFormData,
            task_title: missionFormData.task_title.filter((title, index) => title.trim() !== '' || (missionFormData.task_points[index] !== '' && Number(missionFormData.task_points[index]) !== 0)),
            task_points: missionFormData.task_points
                .map(p => p === '' ? null : Number(p))
                .filter((point, index) => missionFormData.task_title[index].trim() !== '' || (point !== null && point !== 0)),
        };
        apiData.task_points = apiData.task_points.map(p => p === null ? 0 : p);

        try {
            if (isEditMode && currentMissionId) {
                await updateMission(currentMissionId, apiData);
            } else {
                await createMission(apiData);
            }
            closeMissionModal();
            fetchMissions(); // Refresh list
        } catch (err) {
            console.error("Mission form submission error:", err);
            const message = err.errors ? Object.values(err.errors).flat().join(' ') : (err.message || 'Terjadi kesalahan.');
            setFormError(message);
        } finally {
            setFormProcessing(false);
        }
    };

    const handleDeleteMission = async (missionId) => {
        if (!window.confirm(`Yakin hapus misi ID ${missionId}?`)) return;
        setMissionProcessing(true); // Gunakan missionProcessing untuk delete
        setMissionError('');
        try {
            await deleteMission(missionId);
            setMissions(prevMissions => prevMissions.filter(m => m.id !== missionId));
        } catch (err) {
            setMissionError(err.message || 'Gagal menghapus misi.');
            console.error("Delete mission error:", err);
        } finally {
            setMissionProcessing(false);
        }
    };

    // --- Render ---
    if (loadingMissions) {
         return <div className="loading-container"><LoadingSpinner /><span>Loading Missions...</span></div>;
    }

    return (
        <div className="management-page"> {/* Hapus kelas overview-page jika ada */}
            <h2>Manajemen Misi</h2>

            {missionError && <ErrorMessage message={missionError} />}

            <div className="add-button-container">
                <button
                    onClick={() => openMissionModal(false)}
                    disabled={missionProcessing || formProcessing}
                    className="btn btn-primary"
                >
                    Tambah Misi Baru
                </button>
            </div>

            <div className="table-container">
                {missions.length > 0 ? (
                    <table className="mission-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Nama Misi</th>
                                <th>Poin (Tugas Pertama)</th>
                                <th>Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {missions.map((mission) => (
                                <tr key={mission.id}>
                                    <td>{mission.id}</td>
                                    <td>{mission.name}</td>
                                    <td>{mission.points}</td>
                                    <td className="action-cell">
                                        {/* Tombol Progres Pengguna dihapus */}
                                        <button
                                            onClick={() => openMissionModal(true, mission)}
                                            disabled={missionProcessing || formProcessing}
                                            className="btn btn-secondary btn-sm"
                                            aria-label={`Edit misi ${mission.name}`}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDeleteMission(mission.id)}
                                            disabled={missionProcessing || formProcessing}
                                            className="btn btn-danger btn-sm"
                                            aria-label={`Hapus misi ${mission.name}`}
                                        >
                                            Hapus
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    !missionProcessing && <div className="no-data-message">
                        <p>Belum ada misi yang ditambahkan.</p>
                    </div>
                )}
            </div>
            {missionProcessing && missions.length > 0 && <ProcessingIndicator message="Processing delete..." />}


            {/* --- Modal for Add/Edit Mission (Tetap sama) --- */}
            {isModalOpen && (
                <div className={`modal-overlay ${isModalOpen ? 'open' : ''}`} onClick={closeMissionModal}>
                     <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                             <h3>{isEditMode ? 'Edit Misi' : 'Tambah Misi Baru'}</h3>
                             <button onClick={closeMissionModal} className="modal-close-btn" aria-label="Close modal">&times;</button>
                        </div>
                        <form onSubmit={handleMissionFormSubmit}>
                           <div className="modal-body">
                                {formError && <ErrorMessage message={formError} />}
                                {/* Form groups */}
                                <div className="form-group">
                                    <label htmlFor="mission_title">Nama Misi</label>
                                    <input type="text" id="mission_title" name="mission_title" value={missionFormData.mission_title} onChange={handleMissionInputChange} required aria-required="true" />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="mission_SBtitle" className="optional">Subtitle Misi</label>
                                    <input type="text" id="mission_SBtitle" name="mission_SBtitle" value={missionFormData.mission_SBtitle} onChange={handleMissionInputChange} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="mission_description" className="optional">Deskripsi Misi</label>
                                    <textarea id="mission_description" name="mission_description" rows="3" value={missionFormData.mission_description} onChange={handleMissionInputChange}></textarea>
                                </div>
                                <hr className="form-divider" />
                                <h4>Tugas Spesifik (Opsional)</h4>
                                {missionFormData.task_title.map((title, index) => (
                                    <div key={index} className="task-group">
                                        <div className="form-group task-item task-item-title">
                                            <label htmlFor={`task_title_${index}`} className="optional">Judul Tugas {index + 1}</label>
                                            <input type="text" id={`task_title_${index}`} value={title} onChange={(e) => handleMissionTaskChange(index, 'task_title', e.target.value)} placeholder="Contoh: Check-in Hari 1" />
                                        </div>
                                        <div className="form-group task-item task-item-points">
                                            <label htmlFor={`task_points_${index}`} className="optional">Poin Tugas {index + 1}</label>
                                            <input type="number" id={`task_points_${index}`} value={missionFormData.task_points[index]} onChange={(e) => handleMissionTaskChange(index, 'task_points', e.target.value)} placeholder="Contoh: 10" min="0" />
                                        </div>
                                        {missionFormData.task_title.length > 1 && (
                                             <button type="button" onClick={() => removeMissionTaskField(index)} className="btn btn-remove-task" title="Hapus Tugas" disabled={formProcessing} aria-label={`Hapus Tugas ${index + 1}`}>&times;</button>
                                        )}
                                    </div>
                                ))}
                                 <button type="button" onClick={addMissionTaskField} className="btn btn-add-task" disabled={formProcessing}>+ Tambah Tugas</button>
                           </div>
                           <div className="modal-footer">
                                <button type="button" onClick={closeMissionModal} className="btn btn-secondary" disabled={formProcessing}>Batal</button>
                                <button type="submit" className="btn btn-primary" disabled={formProcessing}>
                                    {formProcessing ? <ProcessingIndicator message="Menyimpan..." /> : (isEditMode ? 'Simpan Perubahan' : 'Simpan Misi')}
                                </button>
                           </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MissionManagement;
